import styled from "@emotion/styled";
import { Stack, Switch, type TextFieldProps, Typography } from "@mui/material";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type { JSX, MouseEventHandler } from "react";
import { type Control, Controller, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";
import { StyledFormLabelWrapper } from "./FormStyles";

type Props<FormType extends FieldValues> = Omit<TextFieldProps, "onChange" | "onClick"> & {
  readonly name: FieldPath<FormType>;
  readonly label?: string;
  readonly control: Control<FormType, object>;
  readonly errors: FieldErrors<FormType>;
  readonly labelEndAdornment?: JSX.Element;
  readonly onChange?: (checked: boolean) => void;
  readonly onClick?: MouseEventHandler<HTMLButtonElement>;
  readonly disabled?: boolean;
  readonly onDisabledClick?: () => void;
};

const StyledInputWrapper = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  margin-left: ${(props) => props.theme.spacing(-Spacing.M)};

  & > label {
    align-self: stretch;
    align-items: center;
  }
`;

export const FormSwitch = <T extends object>({
  control,
  name,
  label,
  labelEndAdornment,
  onChange: propsOnChange,
  onClick,
  disabled,
  onDisabledClick,
}: Props<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <StyledInputWrapper
            onClick={(e) => {
              if (disabled) {
                e.preventDefault();
                e.stopPropagation();
                onDisabledClick?.();
              }
            }}
          >
            <Switch
              id={name}
              disabled={disabled}
              checked={value}
              onChange={(event) => {
                onChange(event.target.checked);
                propsOnChange?.(event.target.checked);
              }}
              onClick={onClick}
            />
            {!!label && (
              <StyledFormLabelWrapper htmlFor={name}>
                <Stack direction={"row"} gap={1}>
                  <Typography lineHeight={1} fontWeight={500} variant="body2" mb={Spacing.S} margin={0}>
                    {label}
                  </Typography>
                  {labelEndAdornment}
                </Stack>
              </StyledFormLabelWrapper>
            )}
          </StyledInputWrapper>
        );
      }}
    />
  );
};
